<template>
  <page-header-wrapper>
    <a-card :bordered="false" class="booking-page">
      <div class="head-row">
        <div>
          <h2>Tee Time Booking</h2>
          <p>Search customers and book available bays, with POS card payment support</p>
        </div>
        <a-button shape="round"><a-icon type="question-circle" />Help</a-button>
      </div>

      <a-row :gutter="14">
        <a-col :md="7" :sm="24">
          <div class="panel left-panel">
            <div class="panel-title">Customer Lookup</div>
            <div class="lookup-row">
              <a-input
                v-model="queryParam.mobile"
                class="lookup-input"
                placeholder="Member ID, mobile, email, or name"
                allow-clear
                @pressEnter="handleQuery"
              />
              <a-button type="primary" class="lookup-search-btn" @click="handleQuery">Search</a-button>
              <a-button class="add-user-btn" icon="plus" @click="handleAddUser">Add User</a-button>
            </div>
            <div class="hint">Tip: Search supports member ID, phone, email, first/last name, or nickname. You can also use the POS reader.</div>

            <div class="customer-box">
              <div v-if="!hasSearched" class="empty-card"><a-icon type="search" /><p>Search by member ID, mobile, email, or name</p></div>
              <div v-else-if="!currentUser" class="empty-card"><a-icon type="frown" /><p>No customer found</p></div>
              <div v-else class="customer-card">
                <div class="user-head">
                  <div class="avatar">{{ (currentUser.nickname || 'C').slice(0, 1) }}</div>
                  <div>
                    <div class="name">{{ `${currentUser.lastName || ''} ${currentUser.firstName || ''}`.trim() || 'Unnamed User' }}</div>
                    <div class="sub">Customer ID: {{ currentUser.userCode || '-' }}</div>
                  </div>
                </div>
                <div class="line"><span>Mobile</span><span>{{ currentUser.mobile || '-' }}</span></div>
                <div class="line"><span>Email</span><span>{{ currentUser.email || '-' }}</span></div>
                <div class="line money balance-edit-row">
                  <span>Balance</span>
                  <div class="balance-edit-actions">
                    <span class="balance-currency">USD</span>
                    <a-input-number
                      v-model="editBalanceMoney"
                      :min="0"
                      :step="0.01"
                      :precision="2"
                      class="balance-input"
                      placeholder="Amount"
                    />
                    <a-button
                      type="primary"
                      size="small"
                      class="balance-change-btn"
                      :loading="balanceEditLoading"
                      @click="submitBalanceEdit"
                    >Change</a-button>
                  </div>
                </div>
              </div>

              <div v-if="currentUser" class="booking-info">
                <div class="booking-title">Course orders</div>
                <div v-if="userGolfOrderLoading" class="booking-empty">
                  <a-icon type="loading" /> Loading...
                </div>
                <template v-else>
                  <div v-if="!userGolfOrders.length" class="booking-empty">No course orders</div>
                  <div
                    v-else
                    class="booking-time-ranges"
                    :class="{ 'booking-time-ranges--scroll': userGolfOrderRangeItems.length >= 10 }"
                  >
                    <span
                      v-for="(range, idx) in userGolfOrderRangeItems"
                      :key="`ord-range-${idx}`"
                      class="booking-time-pill"
                    >{{ range }}</span>
                  </div>
                </template>
              </div>
            </div>

            <div class="pay-block">
              <div class="panel-title">Order & Payment</div>
              <div class="amount-row amount-row--topup">
                <span class="amount-row-label">Top-up Packages:</span>
                <div class="amount-tags">
                  <div
                    v-for="item in presetAmounts"
                    :key="item.id"
                    class="amount-tag"
                    :class="{ active: selectedPreset && selectedPreset.id === item.id }"
                    @click="selectPreset(item)"
                  >
                    <div class="amount-main">￥{{ item.topUpAmount }}</div>
                    <div class="amount-give">Bonus ￥{{ item.giveMoney }}</div>
                  </div>
                </div>
              </div>

              <div class="amount-row amount-row--custom">
                <span class="amount-row-label">Custom Amount:</span>
                <a-input-number
                  v-model="customAmount"
                  :min="0"
                  :step="0.01"
                  :precision="2"
                  :disabled="!!selectedPreset"
                  placeholder="Enter amount"
                  style="width:180px"
                  @change="onCustomAmountChange"
                />
              </div>

              <div class="footer-actions">
                <a-button @click="resetAmount">Cancel</a-button>
                <a-button type="primary" :loading="submitLoading" :disabled="!currentUser || finalAmount <= 0" @click="submitRecharge">Top Up Account</a-button>
              </div>
            </div>
          </div>
        </a-col>

        <a-col :md="17" :sm="24">
          <div class="panel right-panel">
            <div class="map-header">
              <div>
                <div class="panel-title">Course List</div>
                <div class="map-sub">Select one course and click Place Order</div>
              </div>
              <div class="legend">
                <a-button
                  class="order-btn"
                  type="primary"
                  :disabled="submitOrderLoading"
                  :loading="submitOrderLoading"
                  @click="submitOrder"
                >
                  <a-icon type="plus-circle" />Place Order
                </a-button>
              </div>
            </div>
            <div class="pitch-list">
              <div
                v-for="item in pitchOptions"
                :key="item.id"
                class="pitch-item"
                :class="{ active: selectedPitchId === item.id }"
                @click="selectedPitchId = item.id"
              >
                {{ item.pitchName }}
              </div>
              <div v-if="!pitchLoading && pitchOptions.length === 0" class="pitch-empty">
                No course data
              </div>
            </div>

            <div class="price-group-block">
              <div class="panel-title">Package List</div>
              <div v-if="pitchPriceLoading" class="price-loading">
                <a-icon type="loading" /> Loading...
              </div>
              <div v-else-if="pitchPriceGroups.length === 0" class="pitch-empty">
                No package data
              </div>
              <div v-else class="price-groups">
                <div v-for="group in pitchPriceGroups" :key="`weekday-${group.weekDay}`" class="price-week-group">
                  <div class="weekday-title">{{ pitchWeekDayLabel(group.weekDay) }}</div>
                  <div class="weekday-items">
                    <div
                      v-for="item in group.prices"
                      :key="`${group.weekDay}-${item.id || `${item.beginTime}-${item.endTime}`}`"
                      class="weekday-item"
                      :class="{
                        active: isPackageSelected(group, item),
                        disabled: customOrderAmount !== null && customOrderAmount !== undefined && Number(customOrderAmount) > 0
                      }"
                      @click="selectPackage(group, item)"
                    >
                      <span>{{ item.beginTime }} - {{ item.endTime }}</span>
                      <b>USD {{ item.prices }}</b>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="order-form-block">
              <div class="panel-title">Order Details</div>
              <div class="order-form-row">
                <span>Start Time:</span>
                <a-date-picker
                  v-model="orderStartTime"
                  style="width: 220px"
                  format="YYYY-MM-DD HH:mm:ss"
                  valueFormat="YYYY-MM-DD HH:mm:ss"
                  :show-time="{ format: 'HH:mm:ss' }"
                  allow-clear
                />
              </div>
              <div class="order-form-row">
                <span>End Time:</span>
                <a-date-picker
                  v-model="orderEndTime"
                  style="width: 220px"
                  format="YYYY-MM-DD HH:mm:ss"
                  valueFormat="YYYY-MM-DD HH:mm:ss"
                  :show-time="{ format: 'HH:mm:ss' }"
                  allow-clear
                />
              </div>
              <div class="order-form-row">
                <span>Order Amount:</span>
                <a-input-number
                  v-model="customOrderAmount"
                  :min="0"
                  :step="0.01"
                  :precision="2"
                  placeholder="Enter order amount"
                  style="width: 220px"
                  :disabled="selectedPackages.length > 0"
                  @change="onOrderAmountChange"
                />
                <span class="order-hint">You can select multiple packages (total amount is summed). Manual amount disables package selection; entering an amount clears package selection.</span>
              </div>
            </div>

          </div>
        </a-col>
      </a-row>

      <add-user-form ref="addUserForm" @ok="handleAddUserSuccess" />
    </a-card>
  </page-header-wrapper>
</template>

<script>
import { pageUser } from '@/api/biz/user'
import { addAccount, editMoney } from '@/api/biz/account'
import { listPitch } from '@/api/golf/pitch'
import { groupPitchPriceByWeekDay } from '@/api/golf/pitchPrice'
import { addOrder, getUserOrderList } from '@/api/golf/order'
import { listRechargeMenu } from '@/api/golf/rechargeMenu'
import AddUserForm from './modules/AddUserForm.vue'

export default {
  name: 'User',
  components: {
    AddUserForm
  },
  data () {
    return {
      queryParam: { mobile: null, pageNum: 1, pageSize: 10 },
      list: [],
      hasSearched: false,
      submitLoading: false,
      pitchLoading: false,
      pitchOptions: [],
      selectedPitchId: null,
      pitchPriceLoading: false,
      pitchPriceGroups: [],
      submitOrderLoading: false,
      /** 多选套餐，每项含 key/weekDay/id/prices 等 */
      selectedPackages: [],
      customOrderAmount: null,
      orderStartTime: null,
      orderEndTime: null,
      presetAmounts: [],
      selectedPreset: null,
      customAmount: null,
      userGolfOrders: [],
      userGolfOrderLoading: false,
      editBalanceMoney: null,
      balanceEditLoading: false
    }
  },
  computed: {
    currentUser () { return this.list.length > 0 ? this.list[0] : null },
    /** 每条订单：2026-05-16 4pm-9pm 32$（日期 + 时段 + payMoney$） */
    userGolfOrderRangeItems () {
      const rows = this.userGolfOrders
      if (!Array.isArray(rows) || !rows.length) return []
      return rows.map((o) => this.formatOrderBookingDisplay(o))
    },
    finalAmount () {
      if (this.customAmount && Number(this.customAmount) > 0) {
        return Number(this.customAmount)
      }
      if (this.selectedPreset) {
        return Number(this.selectedPreset.topUpAmount || 0) + Number(this.selectedPreset.giveMoney || 0)
      }
      return 0
    },
    selectedPitchName () {
      const target = this.pitchOptions.find(item => item.id === this.selectedPitchId)
      return target ? target.pitchName : ''
    },
    orderPayAmount () {
      if (this.selectedPackages.length > 0) {
        return this.selectedPackages.reduce((sum, p) => sum + Number(p.prices || 0), 0)
      }
      return Number(this.customOrderAmount || 0)
    }
  },
  created () {
    this.loadPitchList()
    this.loadRechargeMenuOptions()
  },
  watch: {
    selectedPitchId: {
      handler (val) {
        if (val) {
          this.loadPitchPriceGroups()
        } else {
          this.pitchPriceGroups = []
        }
      },
      immediate: false
    },
    currentUser: {
      handler (u) {
        if (u && u.id != null) {
          const m = u.money
          this.editBalanceMoney = m !== undefined && m !== null ? Number(m) : 0
        } else {
          this.editBalanceMoney = null
        }
      },
      immediate: true,
      deep: true
    }
  },
  methods: {
    submitBalanceEdit () {
      if (!this.currentUser || this.currentUser.id == null) return
      const money = this.editBalanceMoney
      if (money === null || money === undefined || Number.isNaN(Number(money))) {
        this.$message.warning('Please enter a valid balance')
        return
      }
      const n = Number(money)
      if (n < 0) {
        this.$message.warning('Balance cannot be negative')
        return
      }
      this.balanceEditLoading = true
      editMoney({ userId: this.currentUser.id, money: n })
        .then(() => {
          this.$message.success('Balance updated')
          return this.refreshUserInfo()
        })
        .finally(() => {
          this.balanceEditLoading = false
        })
    },
    refreshUserInfo () {
      if (!this.queryParam.mobile) {
        this.userGolfOrders = []
        return Promise.resolve()
      }
      return pageUser(this.queryParam).then((response) => {
        this.list = response.data.records || []
        const u = this.list[0]
        if (u && u.id != null) {
          this.loadUserGolfOrders(u.id)
        } else {
          this.userGolfOrders = []
        }
      })
    },
    loadUserGolfOrders (userId) {
      if (userId === undefined || userId === null) {
        this.userGolfOrders = []
        return
      }
      this.userGolfOrderLoading = true
      getUserOrderList({ userId })
        .then((response) => {
          const raw = response.data
          let rows = []
          if (Array.isArray(raw)) {
            rows = raw
          } else if (raw && Array.isArray(raw.records)) {
            rows = raw.records
          } else if (raw && Array.isArray(raw.list)) {
            rows = raw.list
          }
          this.userGolfOrders = rows
        })
        .catch(() => {
          this.userGolfOrders = []
        })
        .finally(() => {
          this.userGolfOrderLoading = false
        })
    },
    formatOrderBookingDisplay (o) {
      const start = this.parseOrderToDate(o.startTime)
      const end = this.parseOrderToDate(o.endTime)
      if (!start || !end) return '-'
      const dateStr = this.formatOrderDateYmd(start)
      const t1 = this.formatOrderTimeAmPm(start)
      const t2 = this.formatOrderTimeAmPm(end)
      const payRaw = o.payMoney !== undefined && o.payMoney !== null ? o.payMoney : o.pay_money
      const payNum = Number(payRaw)
      const moneyPart = Number.isFinite(payNum) ? ` ${payNum}$` : ''
      return `${dateStr} ${t1}-${t2}${moneyPart}`
    },
    /** 解析订单 LocalDateTime / 字符串 / 时间戳 */
    parseOrderToDate (val) {
      if (val == null || val === '') return null
      if (val instanceof Date) return isNaN(val.getTime()) ? null : val
      if (typeof val === 'number') {
        const d = new Date(val)
        return isNaN(d.getTime()) ? null : d
      }
      let s = String(val).trim()
      if (/^\d+$/.test(s)) {
        const n = parseInt(s, 10)
        const d = new Date(s.length <= 10 ? n * 1000 : n)
        return isNaN(d.getTime()) ? null : d
      }
      if (s.includes(' ') && !s.includes('T')) {
        s = s.replace(' ', 'T')
      }
      const d = new Date(s)
      return isNaN(d.getTime()) ? null : d
    },
    formatOrderDateYmd (d) {
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    },
    /** 如 4pm、9pm、5am、12am（与套餐展示风格一致，小写 am/pm） */
    formatOrderTimeAmPm (d) {
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
      let h = d.getHours()
      const m = d.getMinutes()
      const isPm = h >= 12
      let h12 = h % 12
      if (h12 === 0) h12 = 12
      const ap = isPm ? 'pm' : 'am'
      if (m === 0) {
        return `${h12}${ap}`
      }
      return `${h12}:${pad(m)}${ap}`
    },
    loadPitchList () {
      this.pitchLoading = true
      listPitch({})
        .then(response => {
          this.pitchOptions = response.data || []
          const defaultPitchId = '352352323423'
          const matched = this.pitchOptions.find(item => String(item.id) === defaultPitchId)
          this.selectedPitchId = matched ? matched.id : (this.pitchOptions[0] ? this.pitchOptions[0].id : null)
          if (this.selectedPitchId) {
            this.loadPitchPriceGroups(this.selectedPitchId)
          }
        })
        .finally(() => {
          this.pitchLoading = false
        })
    },
    /** Package time slots display order: 6am-10am → 10am-4pm → 4pm-9pm → 9pm-12am */
    packageTimeSortOrder (item) {
      const a = String(item.beginTime || '').toLowerCase().replace(/\s+/g, '')
      const b = String(item.endTime || '').toLowerCase().replace(/\s+/g, '')
      const key = `${a}-${b}`
      const orderMap = {
        '6am-10am': 0,
        '10am-4pm': 1,
        '4pm-9pm': 2,
        '9pm-12am': 3
      }
      if (orderMap[key] !== undefined) return orderMap[key]
      // 兼容带空格或其它写法
      const label = `${a}-${b}`
      if (label.includes('6am') && label.includes('10am') && label.indexOf('6am') < label.indexOf('10am')) return 0
      if (label.includes('10am') && label.includes('4pm')) return 1
      if (label.includes('4pm') && label.includes('9pm')) return 2
      if (label.includes('9pm') && (label.includes('12am') || label.includes('12:00am'))) return 3
      return 50
    },
    sortPackagePrices (prices) {
      return [...prices].sort((x, y) => this.packageTimeSortOrder(x) - this.packageTimeSortOrder(y))
    },
    loadPitchPriceGroups (pitchId) {
      const targetPitchId = pitchId || this.selectedPitchId
      if (!targetPitchId) {
        this.pitchPriceGroups = []
        this.orderStartTime = null
        this.orderEndTime = null
        return
      }
      this.pitchPriceLoading = true
      this.selectedPackages = []
      this.orderStartTime = null
      this.orderEndTime = null
      groupPitchPriceByWeekDay({ pitchId: targetPitchId }).then((response) => {
        const payload = response.data
        let rawGroups = []
        if (Array.isArray(payload)) {
          rawGroups = payload
        } else if (Array.isArray(payload?.records)) {
          rawGroups = payload.records
        } else if (payload && typeof payload === 'object') {
          rawGroups = Object.keys(payload).map(key => ({
            weekDay: key,
            list: Array.isArray(payload[key]) ? payload[key] : []
          }))
        }

        this.pitchPriceGroups = rawGroups.map(group => {
          const prices = Array.isArray(group.list)
            ? group.list
            : (Array.isArray(group.items) ? group.items : (Array.isArray(group.prices) ? group.prices : []))
          const mapped = prices.map(item => ({
            id: item.id,
            beginTime: item.beginTime || item.startTime || '-',
            endTime: item.endTime || item.stopTime || '-',
            prices: item.prices !== undefined && item.prices !== null ? item.prices : item.price
          }))
          return {
            weekDay: group.weekDay || group.day || group.week || '-',
            prices: this.sortPackagePrices(mapped)
          }
        }).filter(group => group.prices.length > 0)
      }).finally(() => {
        this.pitchPriceLoading = false
      })
    },
    loadRechargeMenuOptions () {
      listRechargeMenu({}).then((response) => {
        const rows = Array.isArray(response.data?.records) ? response.data.records : (Array.isArray(response.data) ? response.data : [])
        this.presetAmounts = rows.map(item => ({
          id: item.id || `${item.topUpAmount || item.top_up_amount}-${item.giveMoney || item.give_money}`,
          topUpAmount: Number(item.topUpAmount !== undefined ? item.topUpAmount : item.top_up_amount) || 0,
          giveMoney: Number(item.giveMoney !== undefined ? item.giveMoney : item.give_money) || 0
        }))
        this.selectedPreset = null
      })
    },
    handleAddUser () {
      if (this.$refs.addUserForm) {
        this.$refs.addUserForm.handleAdd()
      }
    },
    handleAddUserSuccess () {
      this.refreshUserInfo()
    },
    handleQuery () {
      const keyword = this.queryParam.mobile ? String(this.queryParam.mobile).trim() : ''
      if (!keyword) {
        this.$message.warning('Please enter member ID, mobile, email, or name before searching')
        this.hasSearched = false
        this.list = []
        this.userGolfOrders = []
        return
      }
      this.hasSearched = true
      this.refreshUserInfo()
    },
    selectPreset (item) {
      if (this.selectedPreset && this.selectedPreset.id === item.id) {
        this.selectedPreset = null
        return
      }
      this.selectedPreset = item
      this.customAmount = null
    },
    onCustomAmountChange (val) {
      if (val !== null && val !== undefined && Number(val) > 0) {
        this.selectedPreset = null
      }
    },
    resetAmount () { this.selectedPreset = null; this.customAmount = null },
    /** 1=Monday … 7=Sunday，与 golf week_day / Java DayOfWeek 一致 */
    pitchWeekDayLabel (weekDay) {
      const names = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
      const n = Number(weekDay)
      if (Number.isFinite(n) && n >= 1 && n <= 7) return names[n - 1]
      return weekDay != null && weekDay !== '' ? String(weekDay) : ''
    },
    packageRowKey (group, item) {
      const wd = group.weekDay != null ? String(group.weekDay) : ''
      const idPart = item.id != null ? String(item.id) : `${item.beginTime}-${item.endTime}`
      return `${wd}__${idPart}`
    },
    isPackageSelected (group, item) {
      const key = this.packageRowKey(group, item)
      return this.selectedPackages.some(p => p.key === key)
    },
    selectPackage (group, item) {
      if (this.customOrderAmount !== null && this.customOrderAmount !== undefined && Number(this.customOrderAmount) > 0) {
        return
      }
      const key = this.packageRowKey(group, item)
      const idx = this.selectedPackages.findIndex(p => p.key === key)
      if (idx >= 0) {
        this.selectedPackages.splice(idx, 1)
      } else {
        this.selectedPackages.push({
          key,
          id: item.id,
          weekDay: group.weekDay,
          prices: Number(item.prices || 0),
          beginTime: item.beginTime,
          endTime: item.endTime
        })
      }
      this.$nextTick(() => this.syncOrderTimesFromPackageSelection())
    },
    /** JS Date.getDay(): Sun=0 … Sat=6 → ISO 与后端 week_day 一致：Mon=1 … Sun=7 */
    todayIsoWeekday () {
      const js = new Date().getDay()
      return js === 0 ? 7 : js
    },
    /**
     * 解析套餐时段文案（如 6am、10:30am、4pm）为当天分钟数 0–1439
     */
    parsePitchTimeToMinutes (raw) {
      const t = String(raw || '').trim().toLowerCase()
      if (!t || t === '-') return null
      const compact = t.replace(/\s+/g, '')
      const m24 = compact.match(/^(\d{1,2}):(\d{2})$/)
      if (m24) {
        const h = parseInt(m24[1], 10)
        const mi = parseInt(m24[2], 10)
        if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) return h * 60 + mi
      }
      const m12 = compact.match(/^(\d{1,2})(?::(\d{2}))?(am|pm)$/)
      if (m12) {
        let h = parseInt(m12[1], 10)
        const mi = m12[2] ? parseInt(m12[2], 10) : 0
        const ap = m12[3]
        if (ap === 'am') {
          if (h === 12) h = 0
        } else if (h !== 12) {
          h += 12
        }
        if (h >= 0 && h <= 23 && mi >= 0 && mi <= 59) return h * 60 + mi
      }
      return null
    },
    formatDateTimeLocal (d) {
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    /**
     * 按套餐 weekDay（1=周一…7=周日）与 begin/end 时段，落到最近一个对应星期几的日期；
     * 若选的是「今天同一星期几」但时段已结束，则顺延一周。
     */
    computePackageBookingRange (weekDayRaw, beginTime, endTime) {
      const targetIso = Number(weekDayRaw)
      if (!Number.isFinite(targetIso) || targetIso < 1 || targetIso > 7) {
        return null
      }
      const todayIso = this.todayIsoWeekday()
      let addDays = (targetIso - todayIso + 7) % 7
      const startMin = this.parsePitchTimeToMinutes(beginTime)
      const endMin = this.parsePitchTimeToMinutes(endTime)
      if (startMin === null || endMin === null) {
        return null
      }
      const base = new Date()
      base.setHours(0, 0, 0, 0)
      base.setDate(base.getDate() + addDays)
      const startDt = new Date(base)
      startDt.setHours(Math.floor(startMin / 60), startMin % 60, 0, 0)
      let endDt = new Date(base)
      endDt.setHours(Math.floor(endMin / 60), endMin % 60, 0, 0)
      if (endMin <= startMin) {
        endDt.setDate(endDt.getDate() + 1)
      }
      const now = new Date()
      if (addDays === 0 && now >= endDt) {
        startDt.setDate(startDt.getDate() + 7)
        endDt.setDate(endDt.getDate() + 7)
      }
      return { startDt, endDt }
    },
    /**
     * 根据已选套餐（取列表第一项）写入下单开始/结束时间，供接口 startTime、endTime
     */
    syncOrderTimesFromPackageSelection () {
      if (this.customOrderAmount !== null && this.customOrderAmount !== undefined && Number(this.customOrderAmount) > 0) {
        return
      }
      if (!this.selectedPackages.length) {
        this.orderStartTime = null
        this.orderEndTime = null
        return
      }
      const p = this.selectedPackages[0]
      const range = this.computePackageBookingRange(p.weekDay, p.beginTime, p.endTime)
      if (!range) {
        return
      }
      this.orderStartTime = this.formatDateTimeLocal(range.startDt)
      this.orderEndTime = this.formatDateTimeLocal(range.endDt)
    },
    onOrderAmountChange (val) {
      if (val !== null && val !== undefined && Number(val) > 0) {
        this.selectedPackages = []
        this.syncOrderTimesFromPackageSelection()
      }
    },
    submitOrder () {
      if (!this.currentUser) {
        this.$message.warning('No user selected')
        return
      }
      if (!this.selectedPitchId) {
        this.$message.warning('Please select a course first')
        return
      }
      if (this.orderPayAmount <= 0) {
        this.$message.warning('No amount provided')
        return
      }
      if (!this.orderStartTime || !this.orderEndTime) {
        this.$message.warning('Please select package slots (start/end time will be filled automatically) or set start and end time manually')
        return
      }
      this.submitOrderLoading = true
      addOrder({
        userId: this.currentUser.id,
        pitchId: this.selectedPitchId,
        startTime: this.orderStartTime,
        endTime: this.orderEndTime,
        payMoney: this.orderPayAmount
      }).then(() => {
        this.$message.success('Order placed successfully')
        this.selectedPackages = []
        this.customOrderAmount = null
        this.orderStartTime = null
        this.orderEndTime = null
        return this.refreshUserInfo()
      }).finally(() => {
        this.submitOrderLoading = false
      })
    },
    submitRecharge () {
      if (!this.currentUser) return this.$message.warning('Please search for a customer first')
      const money = Number(this.finalAmount)
      if (money <= 0) return this.$message.warning('Please select or enter a top-up amount')
      this.submitLoading = true
      addAccount({ userId: this.currentUser.id, money }).then(() => {
        this.$message.success('Top up successful')
        return this.refreshUserInfo()
      }).finally(() => { this.submitLoading = false })
    }
  }
}
</script>

<style lang="less" scoped>
.booking-page {
  background: linear-gradient(135deg, #f4f7fb 0%, #eef3ff 45%, #f8faff 100%);
  border-radius: 14px;

  .head-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 12px;
  }
  .head-row h2 {
    margin: 0;
    font-size: 34px;
    font-weight: 700;
  }
  .head-row p {
    margin: 4px 0 0;
    color: #6b7280;
  }
  .panel {
    border: 1px solid #d7deeb;
    border-radius: 14px;
    background: linear-gradient(180deg, #f7f8fa 0%, #f1f3f5 100%);
    box-shadow: 0 8px 24px rgba(15, 23, 42, 0.08);
    padding: 16px;
    min-height: 600px;
  }
  .panel-title {
    font-size: 22px;
    font-weight: 700;
    color: #111827;
  }
  .lookup-row {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
  }
  .lookup-input {
    flex: 1;
    min-width: 0;
  }
  .lookup-search-btn,
  .add-user-btn {
    flex-shrink: 0;
  }
  .add-user-btn {
    color: #fff;
    border-color: #52c41a;
    background: #52c41a;
  }
  .add-user-btn:hover,
  .add-user-btn:focus {
    color: #fff;
    border-color: #73d13d;
    background: #73d13d;
  }
  .hint {
    margin-top: 8px;
    color: #9ca3af;
    font-size: 12px;
  }
  .customer-box {
    margin-top: 12px;
  }
  .empty-card {
    min-height: 220px;
    border: 1px dashed #c5cedf;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #8c8c8c;
  }
  .empty-card .anticon {
    font-size: 28px;
    margin-bottom: 8px;
  }
  .empty-card p {
    margin: 0;
  }
  .customer-card {
    background: linear-gradient(160deg, #eef5ff 0%, #f8fbff 100%);
    border: 1px solid #cddcf8;
    border-radius: 10px;
    padding: 12px;
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.7);
  }
  .customer-card .user-head {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
  }
  .customer-card .avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #2563eb;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 10px;
    font-weight: 700;
  }
  .customer-card .name {
    font-weight: 700;
  }
  .customer-card .sub {
    color: #6b7280;
    font-size: 12px;
  }
  .customer-card .line {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .customer-card .money {
    color: #16a34a;
    font-weight: 700;
    margin-top: 8px;
  }
  .customer-card .balance-edit-row {
    align-items: center;
    flex-wrap: wrap;
    gap: 8px 0;
  }
  .customer-card .balance-edit-actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex-wrap: wrap;
    gap: 6px 8px;
    max-width: 100%;
  }
  .customer-card .balance-currency {
    color: #16a34a;
    font-weight: 600;
    font-size: 13px;
  }
  .customer-card .balance-input {
    width: 122px;
  }
  .customer-card .balance-change-btn {
    flex-shrink: 0;
  }
  .booking-info {
    margin-top: 14px;
  }
  .booking-title {
    font-size: 18px;
    font-weight: 700;
    margin-bottom: 8px;
    color: #111827;
  }
  .booking-list {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .booking-item {
    padding: 8px 10px;
    background: #f6ffed;
    border: 1px solid #d9f7be;
    border-radius: 6px;
    color: #237804;
    font-size: 13px;
  }
  .booking-empty {
    color: #9ca3af;
    font-size: 13px;
  }
  .booking-time-ranges {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
  }
  /** 满 10 条及以上：可视高度约 10 行，超出纵向滚动 */
  .booking-time-ranges--scroll {
    max-height: 420px;
    overflow-y: auto;
    overflow-x: hidden;
    padding-right: 4px;
  }
  .booking-time-ranges--scroll::-webkit-scrollbar {
    width: 6px;
  }
  .booking-time-ranges--scroll::-webkit-scrollbar-thumb {
    background: rgba(82, 196, 26, 0.45);
    border-radius: 3px;
  }
  .booking-time-pill {
    display: block;
    width: 100%;
    box-sizing: border-box;
    padding: 6px 12px;
    font-size: 13px;
    line-height: 1.45;
    color: #276749;
    word-break: break-word;
    border: 1px solid #73d13d;
    border-radius: 8px;
    background: #f6ffed;
    box-shadow: 0 1px 2px rgba(82, 196, 26, 0.12);
  }
  .map-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
  }
  .map-sub {
    color: #6b7280;
    margin-top: 4px;
    font-size: 14px;
  }
  .legend {
    display: flex;
    gap: 12px;
    color: #6b7280;
    font-size: 12px;
  }
  .legend .ant-btn {
    height: 40px;
    padding: 0 18px;
    font-size: 15px;
  }
  .order-btn {
    height: 52px !important;
    padding: 0 34px !important;
    font-size: 19px !important;
    font-weight: 700;
    border-radius: 12px !important;
    border: none !important;
    background: linear-gradient(135deg, #1f9d55 0%, #34c759 60%, #5adf7d 100%) !important;
    box-shadow: 0 12px 24px rgba(31, 157, 85, 0.4);
    transition: all 0.2s ease;
  }
  .order-btn:hover,
  .order-btn:focus {
    transform: translateY(-2px) scale(1.01);
    box-shadow: 0 16px 28px rgba(31, 157, 85, 0.5);
  }
  .order-btn[disabled] {
    box-shadow: none !important;
    background: #d9d9d9 !important;
    transform: none !important;
  }
  .pitch-list {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 12px;
    margin-bottom: 32px;
  }
  .pitch-item {
    min-width: 140px;
    padding: 12px 14px;
    border: 1px solid #cfd7e6;
    border-radius: 8px;
    cursor: pointer;
    user-select: none;
    text-align: center;
    font-size: 15px;
  }
  .pitch-item.active {
    border-color: #3b82f6;
    color: #2563eb;
    background: linear-gradient(135deg, #eaf3ff 0%, #dbeafe 100%);
    font-weight: 600;
    box-shadow: 0 6px 12px rgba(37, 99, 235, 0.18);
  }
  .pitch-empty {
    color: #9ca3af;
    font-size: 14px;
  }
  .price-group-block {
    margin-bottom: 24px;
    border: 1px solid #d7deeb;
    border-radius: 10px;
    padding: 14px;
    background: #f8fafc;
  }
  .price-group-block .panel-title {
    font-size: 18px;
    margin-bottom: 10px;
  }
  .price-loading {
    color: #6b7280;
    font-size: 13px;
  }
  .price-groups {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
  .price-week-group {
    border: 1px solid #dbe4f2;
    border-radius: 8px;
    padding: 10px;
    background: #fff;
  }
  .weekday-title {
    font-weight: 700;
    color: #1f2937;
    margin-bottom: 8px;
  }
  .weekday-items {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
  }
  .weekday-item {
    min-width: 220px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    padding: 8px 10px;
    border-radius: 6px;
    border: 1px solid #e5e7eb;
    background: #f9fafb;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.2s ease;
  }
  .weekday-item:hover {
    border-color: #93c5fd;
    background: #eff6ff;
  }
  .weekday-item.active {
    border-color: #3b82f6;
    background: #dbeafe;
    box-shadow: 0 4px 10px rgba(37, 99, 235, 0.18);
  }
  .weekday-item.disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
  .weekday-item b {
    color: #16a34a;
  }
  .order-form-block {
    border: 1px solid #d7deeb;
    border-radius: 10px;
    padding: 14px;
    background: #f8fafc;
    margin-bottom: 24px;
  }
  .order-form-row {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-top: 12px;
    flex-wrap: wrap;
  }
  .order-form-row:first-of-type {
    margin-top: 8px;
  }
  .order-hint {
    color: #6b7280;
    font-size: 12px;
  }
  .dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 4px;
  }
  .dot.free { background: #52c41a; }
  .dot.soon { background: #fadb14; }
  .dot.used { background: #ff4d4f; }
  .dot.maintain { background: #1890ff; }
  .pay-block {
    border: 1px solid #d7deeb;
    border-radius: 10px;
    padding: 20px;
    background: #f3f4f6;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.9);
  }
  .price-line {
    display: flex;
    justify-content: space-between;
    margin-bottom: 6px;
  }
  .discount {
    color: #ff4d4f;
  }
  .total {
    margin-top: 8px;
    font-weight: 700;
    font-size: 28px;
    color: #cf1322;
  }
  .pay-mode-title {
    margin: 10px 0 8px;
    font-weight: 600;
  }
  .pay-mode {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 8px;
    margin-bottom: 10px;
  }
  .pay-btn {
    height: 42px;
  }
  .pay-btn.active {
    border-color: #3b82f6;
    color: #2563eb;
  }
  .amount-row {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 10px 12px;
    margin-bottom: 16px;
    font-size: 15px;
    min-height: 44px;
  }
  .amount-row-label {
    flex: 0 0 auto;
    min-width: 0;
  }
  /** 窄栏：标签独占一行，避免与「Top-up Packages」横排挤爆 */
  .amount-row--topup {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }
  .amount-row--topup .amount-tags {
    margin-left: 0;
    width: 100%;
    max-width: 100%;
  }
  .amount-row--custom {
    align-items: center;
  }
  .amount-row--custom .amount-row-label {
    margin-right: 4px;
  }
  .amount-tags {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(96px, 1fr));
    gap: 8px;
    margin-left: 0;
    min-width: 0;
  }
  .amount-tag {
    cursor: pointer;
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
    height: 68px;
    border-radius: 10px;
    padding: 9px 6px 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    background: #1f1f1f;
    border: 1px solid #303030;
    transition: all 0.2s ease;
  }
  .amount-main {
    color: #ffffff;
    font-size: 20px;
    line-height: 1.05;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
  .amount-give {
    margin-top: 4px;
    color: #ffffff;
    font-size: 12px;
    line-height: 1.2;
    font-weight: 400;
    opacity: 0.95;
  }
  .amount-tag:hover {
    border-color: #434343;
    box-shadow: 0 6px 14px rgba(0, 0, 0, 0.35);
  }
  .amount-tag.active {
    background: #52c41a;
    border-color: #52c41a;
    box-shadow: 0 0 0 2px rgba(82, 196, 26, 0.24);
  }
  .amount-tag.active .amount-main {
    color: #ffffff;
  }
  .amount-tag.active .amount-give {
    color: #ffffff;
  }
  .warn {
    border: 1px solid #ffd591;
    background: linear-gradient(180deg, #fff7e6 0%, #fff3d8 100%);
    color: #ad6800;
    border-radius: 8px;
    padding: 10px;
    margin: 12px 0;
  }
  .footer-actions {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 10px;
  }
  .footer-actions .ant-btn {
    height: 42px;
  }
}
</style>
